import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Auth helpers
export const authHelpers = {
  async signUp(email: string, password: string, firstName: string, lastName: string, accountType: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
          account_type: accountType
        }
      }
    });

    if (error) throw error;

    // Create profile
    if (data.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: data.user.id,
          email: data.user.email!,
          first_name: firstName,
          last_name: lastName,
          account_type: accountType
        });

      if (profileError) throw profileError;
    }

    return data;
  },

  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  async getProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  }
};

// Database helpers
export const dbHelpers = {
  // Listings
  async getListings(filters?: { category?: string; location?: string; verified?: boolean }) {
    let query = supabase
      .from('listings')
      .select('*')
      .eq('status', 'approved')
      .order('featured', { ascending: false })
      .order('rating', { ascending: false });

    if (filters?.category) {
      query = query.eq('category', filters.category);
    }
    if (filters?.location) {
      query = query.eq('location', filters.location);
    }
    if (filters?.verified !== undefined) {
      query = query.eq('verified', filters.verified);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getListing(id: string) {
    const { data, error } = await supabase
      .from('listings')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Reviews
  async getReviews(listingId: string) {
    const { data, error } = await supabase
      .from('reviews')
      .select('*, profiles(first_name, last_name)')
      .eq('listing_id', listingId)
      .eq('status', 'approved')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async createReview(listingId: string, userId: string, rating: number, title: string, comment: string) {
    const { data, error } = await supabase
      .from('reviews')
      .insert({
        listing_id: listingId,
        user_id: userId,
        rating,
        title,
        comment
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Favorites
  async getFavorites(userId: string) {
    const { data, error } = await supabase
      .from('favorites')
      .select('*, listings(*)')
      .eq('user_id', userId);

    if (error) throw error;
    return data;
  },

  async addFavorite(userId: string, listingId: string) {
    const { data, error } = await supabase
      .from('favorites')
      .insert({ user_id: userId, listing_id: listingId })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeFavorite(userId: string, listingId: string) {
    const { error } = await supabase
      .from('favorites')
      .delete()
      .eq('user_id', userId)
      .eq('listing_id', listingId);

    if (error) throw error;
  },

  // Checklist
  async getChecklistItems(userId: string) {
    const { data, error } = await supabase
      .from('checklist_items')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  async updateChecklistItem(id: string, completed: boolean) {
    const { data, error } = await supabase
      .from('checklist_items')
      .update({ completed, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async createChecklistItem(userId: string, title: string, category: string) {
    const { data, error } = await supabase
      .from('checklist_items')
      .insert({ user_id: userId, title, category })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Quote Requests
  async createQuoteRequest(listingId: string, name: string, email: string, phone: string, message: string, userId?: string) {
    const { data, error } = await supabase
      .from('quote_requests')
      .insert({
        listing_id: listingId,
        user_id: userId,
        name,
        email,
        phone,
        message
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Analytics
  async trackEvent(eventType: string, listingId?: string, userId?: string, metadata?: any) {
    const { error } = await supabase
      .from('analytics_events')
      .insert({
        event_type: eventType,
        listing_id: listingId,
        user_id: userId,
        metadata
      });

    if (error) console.error('Analytics tracking error:', error);
  },

  // Newsletter
  async subscribeNewsletter(email: string, firstName?: string, source?: string) {
    const { data, error } = await supabase
      .from('newsletter_subscribers')
      .insert({
        email,
        first_name: firstName,
        source
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') { // Unique violation
        throw new Error('This email is already subscribed');
      }
      throw error;
    }
    return data;
  },

  // Blog
  async getBlogPosts(category?: string) {
    let query = supabase
      .from('blog_posts')
      .select('*, profiles(first_name, last_name)')
      .eq('status', 'published')
      .order('published_at', { ascending: false });

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getBlogPost(slug: string) {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*, profiles(first_name, last_name)')
      .eq('slug', slug)
      .eq('status', 'published')
      .single();

    if (error) throw error;

    // Increment view count
    await supabase
      .from('blog_posts')
      .update({ views: (data.views || 0) + 1 })
      .eq('id', data.id);

    return data;
  },

  // Affiliate tracking
  async trackAffiliateClick(listingId: string, userId?: string, metadata?: any) {
    const { data, error } = await supabase
      .from('affiliate_clicks')
      .insert({
        listing_id: listingId,
        user_id: userId,
        metadata
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateAffiliateConversion(clickId: string, conversionValue: number, commissionEarned: number) {
    const { data, error } = await supabase
      .from('affiliate_clicks')
      .update({
        converted: true,
        conversion_value: conversionValue,
        conversion_date: new Date().toISOString(),
        commission_earned: commissionEarned,
        commission_status: 'pending'
      })
      .eq('id', clickId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getAffiliatePrograms(activeOnly: boolean = true) {
    let query = supabase
      .from('listings')
      .select('id, business_name, category, affiliate_commission_rate, affiliate_link, affiliate_status')
      .eq('has_affiliate_program', true);

    if (activeOnly) {
      query = query.eq('affiliate_status', 'active');
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Community Features
  async getForumCategories() {
    const { data, error } = await supabase
      .from('forum_categories')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) throw error;
    return data;
  },

  async getForumThreads(categoryId?: string, limit?: number) {
    let query = supabase
      .from('forum_threads')
      .select('*, profiles(first_name, last_name, avatar_url), forum_categories(name, slug)')
      .order('is_pinned', { ascending: false })
      .order('last_reply_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (categoryId) {
      query = query.eq('category_id', categoryId);
    }

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getForumThread(threadId: string) {
    const { data, error } = await supabase
      .from('forum_threads')
      .select(`
        *,
        profiles:user_id (first_name, last_name, avatar_url),
        forum_categories:category_id (name, slug)
      `)
      .eq('id', threadId)
      .single();

    if (error) throw error;
    
    // Increment view count
    await supabase
      .from('forum_threads')
      .update({ view_count: (data.view_count || 0) + 1 })
      .eq('id', threadId);

    return data;
  },

  async createForumThread(categoryId: string, userId: string, title: string, content: string) {
    const { data, error } = await supabase
      .from('forum_threads')
      .insert({
        category_id: categoryId,
        user_id: userId,
        title,
        content
      })
      .select()
      .single();

    if (error) throw error;

    // Update category post count
    const { data: category } = await supabase
      .from('forum_categories')
      .select('post_count')
      .eq('id', categoryId)
      .single();

    if (category) {
      await supabase
        .from('forum_categories')
        .update({ post_count: (category.post_count || 0) + 1 })
        .eq('id', categoryId);
    }

    return data;
  },

  async getForumReplies(threadId: string) {
    const { data, error } = await supabase
      .from('forum_replies')
      .select(`
        *,
        profiles (first_name, last_name, avatar_url)
      `)
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async createForumReply(threadId: string, authorId: string, content: string) {
    const { error } = await supabase
      .from('forum_replies')
      .insert({
        thread_id: threadId,
        author_id: authorId,
        content,
        upvotes: 0
      });

    if (error) throw error;

    // Update reply count on thread
    const { data: thread } = await supabase
      .from('forum_threads')
      .select('reply_count')
      .eq('id', threadId)
      .single();

    if (thread) {
      await supabase
        .from('forum_threads')
        .update({ reply_count: (thread.reply_count || 0) + 1 })
        .eq('id', threadId);
    }
  },

  async voteOnReply(replyId: string, userId: string, voteType: 'up' | 'down') {
    // Check if user already voted
    const { data: existingVote } = await supabase
      .from('forum_votes')
      .select('*')
      .eq('reply_id', replyId)
      .eq('user_id', userId)
      .single();

    if (existingVote) {
      // Remove vote if clicking same type, otherwise update
      if (existingVote.vote_type === voteType) {
        await supabase
          .from('forum_votes')
          .delete()
          .eq('id', existingVote.id);
      } else {
        await supabase
          .from('forum_votes')
          .update({ vote_type: voteType })
          .eq('id', existingVote.id);
      }
    } else {
      // Create new vote
      await supabase
        .from('forum_votes')
        .insert({
          reply_id: replyId,
          user_id: userId,
          vote_type: voteType
        });
    }

    // Update upvotes count on reply
    const { data: votes } = await supabase
      .from('forum_votes')
      .select('vote_type')
      .eq('reply_id', replyId);

    const upvotes = votes?.filter(v => v.vote_type === 'up').length || 0;
    const downvotes = votes?.filter(v => v.vote_type === 'down').length || 0;

    await supabase
      .from('forum_replies')
      .update({ upvotes: upvotes - downvotes })
      .eq('id', replyId);
  },

  // Community Events
  async getCommunityEvents(filters?: { type?: string; city?: string; status?: string }) {
    let query = supabase
      .from('community_events')
      .select('*, profiles(first_name, last_name, avatar_url)')
      .order('event_date', { ascending: true });

    if (filters?.type) {
      query = query.eq('event_type', filters.type);
    }
    if (filters?.city) {
      query = query.eq('city', filters.city);
    }
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  async getCommunityEvent(id: string) {
    const { data, error } = await supabase
      .from('community_events')
      .select('*, profiles(first_name, last_name, avatar_url)')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  async createCommunityEvent(organizerId: string, eventData: any) {
    const { data, error } = await supabase
      .from('community_events')
      .insert({
        organizer_id: organizerId,
        ...eventData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async rsvpToEvent(eventId: string, userId: string, status: 'going' | 'interested' | 'not_going') {
    const { data: existingRsvp } = await supabase
      .from('event_rsvps')
      .select('*')
      .eq('event_id', eventId)
      .eq('user_id', userId)
      .single();

    if (existingRsvp) {
      // Update existing RSVP
      const { error } = await supabase
        .from('event_rsvps')
        .update({ status })
        .eq('id', existingRsvp.id);

      if (error) throw error;
    } else {
      // Create new RSVP
      const { error } = await supabase
        .from('event_rsvps')
        .insert({ event_id: eventId, user_id: userId, status });

      if (error) throw error;
    }

    // Update event attendee count
    const { data: rsvps } = await supabase
      .from('event_rsvps')
      .select('status')
      .eq('event_id', eventId);

    if (rsvps) {
      const attendeeCount = rsvps.filter(r => r.status === 'going').length;
      await supabase
        .from('community_events')
        .update({ attendee_count: attendeeCount })
        .eq('id', eventId);
    }
  },

  async getEventRsvps(eventId: string) {
    const { data, error } = await supabase
      .from('event_rsvps')
      .select('*, profiles(first_name, last_name, avatar_url)')
      .eq('event_id', eventId);

    if (error) throw error;
    return data;
  },

  // User Connections
  async sendConnectionRequest(userId: string, connectedUserId: string) {
    const { data, error } = await supabase
      .from('user_connections')
      .insert({
        user_id: userId,
        connected_user_id: connectedUserId,
        status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async acceptConnectionRequest(connectionId: string) {
    const { data, error } = await supabase
      .from('user_connections')
      .update({ status: 'accepted' })
      .eq('id', connectionId)
      .select()
      .single();

    if (error) throw error;

    // Update connection counts for both users
    const connection = data;
    await Promise.all([
      supabase.rpc('increment_connection_count', { user_id: connection.user_id }),
      supabase.rpc('increment_connection_count', { user_id: connection.connected_user_id })
    ]);

    return data;
  },

  async getUserConnections(userId: string) {
    const { data, error } = await supabase
      .from('user_connections')
      .select('*, profiles!user_connections_connected_user_id_fkey(first_name, last_name, avatar_url, location)')
      .eq('user_id', userId)
      .eq('status', 'accepted');

    if (error) throw error;
    return data;
  },

  // Relocation Progress
  async getRelocationProgress(userId: string) {
    const { data, error } = await supabase
      .from('relocation_progress')
      .select('*')
      .eq('user_id', userId)
      .order('order_index', { ascending: true });

    if (error) throw error;
    return data;
  },

  async createRelocationTask(userId: string, taskData: any) {
    const { data, error } = await supabase
      .from('relocation_progress')
      .insert({
        user_id: userId,
        ...taskData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateRelocationTask(taskId: string, updates: any) {
    const { data, error } = await supabase
      .from('relocation_progress')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', taskId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteRelocationTask(taskId: string) {
    const { error } = await supabase
      .from('relocation_progress')
      .delete()
      .eq('id', taskId);

    if (error) throw error;
  },

  // Budget Tracker
  async getBudgetItems(userId: string) {
    const { data, error } = await supabase
      .from('budget_items')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async createBudgetItem(userId: string, itemData: any) {
    const { data, error } = await supabase
      .from('budget_items')
      .insert({
        user_id: userId,
        ...itemData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateBudgetItem(itemId: string, updates: any) {
    const { data, error } = await supabase
      .from('budget_items')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteBudgetItem(itemId: string) {
    const { error } = await supabase
      .from('budget_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;
  },

  // User Profile Updates
  async updateUserProfile(userId: string, updates: any) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getUserProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  },

  async searchUsers(query: string, filters?: { location?: string; relocationStatus?: string }) {
    let dbQuery = supabase
      .from('profiles')
      .select('id, first_name, last_name, avatar_url, bio, location, current_city, occupation, relocation_status')
      .eq('is_public', true)
      .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,occupation.ilike.%${query}%`)
      .limit(20);

    if (filters?.location) {
      dbQuery = dbQuery.eq('current_city', filters.location);
    }
    if (filters?.relocationStatus) {
      dbQuery = dbQuery.eq('relocation_status', filters.relocationStatus);
    }

    const { data, error } = await dbQuery;
    if (error) throw error;
    return data;
  }
};
